import { GitLabAnthropicLanguageModel } from './gitlab-anthropic-language-model';
import { GitLabOpenAILanguageModel } from './gitlab-openai-language-model';
import { GitLabWorkflowLanguageModel } from './gitlab-workflow-language-model';
import { GitLabError } from './gitlab-error';
import type { LanguageModelV2 } from '@ai-sdk/provider';
import { GitLabOAuthManager } from './gitlab-oauth-manager';
import type { OpenCodeAuth } from './gitlab-oauth-types';
import { getModelMapping, getValidModelsForProvider, isWorkflowModel } from './model-mappings';
import type { GitLabWorkflowOptions } from './gitlab-workflow-types';
import { VERSION } from './version';
import * as fs from 'fs';
import * as path from 'path';
import * as os from 'os';

export interface GitLabProvider {
  (modelId: string): LanguageModelV2;
  readonly specificationVersion: 'v2';
  languageModel(modelId: string): LanguageModelV2;
  chat(modelId: string): LanguageModelV2;
  /**
   * Create an agentic chat model with tool calling support
   *
   * @param modelId - GitLab model identifier. Some IDs automatically map to specific Anthropic models.
   * @param options - Configuration options for the agentic model
   * @returns A language model with native tool calling support via Anthropic
   *
   * @example
   * // Automatic model mapping
   * const model = gitlab.agenticChat('duo-chat-opus-4-5');
   * // Uses claude-opus-4-5-20251101
   *
   * @example
   * // Explicit model override
   * const model = gitlab.agenticChat('duo-chat', {
   *   anthropicModel: 'claude-sonnet-4-5-20250929'
   * });
   */
  agenticChat(modelId: string, options?: GitLabAgenticOptions): GitLabAnthropicLanguageModel;
  /**
   * Create a workflow chat model using GitLab Duo Agent Platform.
   *
   * Workflow models use a server-side agentic loop where GitLab's DWS drives
   * the LLM, requests tool executions from the client via WebSocket, and
   * streams text/status back.
   *
   * Requires GitLab Ultimate with Duo Enterprise add-on.
   *
   * @param modelId - Workflow model identifier (e.g., 'duo-workflow-sonnet-4-6')
   * @param options - Workflow-specific configuration
   * @returns A language model backed by the DWS WebSocket protocol
   *
   * @example
   * const model = gitlab.workflowChat('duo-workflow-sonnet-4-6', {
   *   mcpTools: [...],
   *   preapprovedTools: ['read_file', 'write_file'],
   * });
   */
  workflowChat(modelId: string, options?: GitLabWorkflowOptions): GitLabWorkflowLanguageModel;
  textEmbeddingModel(modelId: string): never;
  imageModel(modelId: string): never;
}

export interface GitLabAgenticOptions {
  /**
   * Override the provider-specific model (optional).
   * Must be a valid model for the detected provider.
   *
   * For Anthropic models:
   * - 'claude-opus-4-6'
   * - 'claude-sonnet-4-6'
   * - 'claude-opus-4-5-20251101'
   * - 'claude-sonnet-4-5-20250929'
   * - 'claude-haiku-4-5-20251001'
   *
   * For OpenAI models:
   * - 'gpt-5.1-2025-11-13'
   * - 'gpt-5-mini-2025-08-07'
   * - 'gpt-5-codex'
   * - 'gpt-5.2-codex'
   *
   * @example
   * // Override with explicit model
   * const model = gitlab.agenticChat('duo-chat-opus-4-5', {
   *   providerModel: 'claude-sonnet-4-5-20250929'
   * });
   */
  providerModel?: string;

  /**
   * Maximum tokens to generate
   * @default 8192
   */
  maxTokens?: number;

  /**
   * Feature flags to pass to the GitLab API
   */
  featureFlags?: Record<string, boolean>;

  /**
   * Custom headers for AI Gateway requests (per-model override).
   * These headers are sent to the Anthropic/OpenAI proxy endpoints.
   * Merged with provider-level aiGatewayHeaders (model-level takes precedence).
   */
  aiGatewayHeaders?: Record<string, string>;
}

export interface GitLabProviderSettings {
  /**
   * GitLab instance URL (e.g., 'https://gitlab.com')
   * Can also be set via GITLAB_INSTANCE_URL environment variable.
   * @default 'https://gitlab.com'
   */
  instanceUrl?: string;

  /**
   * API token (Personal Access Token or OAuth access token)
   * Can also be set via GITLAB_TOKEN environment variable
   */
  apiKey?: string;

  /**
   * OAuth refresh token (optional, for OAuth flow)
   */
  refreshToken?: string;

  /**
   * OAuth client ID (required for OAuth flow)
   */
  clientId?: string;

  /**
   * OAuth redirect URI (required for OAuth flow)
   */
  redirectUri?: string;

  /**
   * Custom headers to include in requests
   */
  headers?: Record<string, string>;

  /**
   * Custom fetch implementation
   */
  fetch?: typeof fetch;

  /**
   * Provider name override
   */
  name?: string;

  /**
   * Default feature flags to pass to the GitLab API for all agentic chat models
   */
  featureFlags?: Record<string, boolean>;

  /**
   * AI Gateway URL for the Anthropic proxy.
   * Can also be set via GITLAB_AI_GATEWAY_URL environment variable.
   * @default 'https://cloud.gitlab.com'
   */
  aiGatewayUrl?: string;

  /**
   * Custom headers to include in AI Gateway requests (Anthropic/OpenAI proxy).
   * These headers are merged with the default headers from direct_access response.
   * Default User-Agent: gitlab-ai-provider/{version}
   */
  aiGatewayHeaders?: Record<string, string>;
}

/**
 * Get OpenCode auth file path
 * Uses XDG Base Directory specification
 */
function getOpenCodeAuthPath(): string {
  const homeDir = os.homedir();

  // Check XDG_DATA_HOME first (Linux/Mac standard)
  const xdgDataHome = process.env.XDG_DATA_HOME;
  if (xdgDataHome) {
    return path.join(xdgDataHome, 'opencode', 'auth.json');
  }

  // Fallback to ~/.local/share/opencode/auth.json (XDG default)
  if (process.platform !== 'win32') {
    return path.join(homeDir, '.local', 'share', 'opencode', 'auth.json');
  }

  // Windows fallback
  return path.join(homeDir, '.opencode', 'auth.json');
}

/**
 * Load OpenCode auth.json file
 */
async function loadOpenCodeAuth(instanceUrl: string): Promise<OpenCodeAuth | undefined> {
  try {
    const authPath = getOpenCodeAuthPath();

    if (!fs.existsSync(authPath)) {
      return undefined;
    }

    const authData = JSON.parse(fs.readFileSync(authPath, 'utf-8'));

    // Priority 1: Check 'gitlab' key (used by opencode-gitlab-auth plugin)
    if (authData.gitlab?.type === 'oauth') {
      const gitlabAuth = authData.gitlab;
      // Verify it matches the requested instance URL
      if (
        gitlabAuth.enterpriseUrl === instanceUrl ||
        gitlabAuth.enterpriseUrl === instanceUrl.replace(/\/$/, '')
      ) {
        return gitlabAuth as OpenCodeAuth;
      }
    }

    // Priority 2: Try to find auth for this instance by URL
    // Check both with and without trailing slash
    const normalizedUrl = instanceUrl.replace(/\/$/, '');
    const auth = authData[normalizedUrl] || authData[`${normalizedUrl}/`];

    return auth as OpenCodeAuth | undefined;
  } catch (error) {
    throw new Error(`Failed to load auth.json: ${error instanceof Error ? error.message : error}`);
  }
}

/**
 * Load GitLab API key with OAuth support
 * Priority: explicit apiKey > OAuth token > env var
 */
async function loadApiKey(
  options: {
    apiKey?: string;
    environmentVariableName: string;
    description: string;
  },
  instanceUrl: string,
  clientId?: string
): Promise<string> {
  // Priority 1: Explicit apiKey
  if (options.apiKey) {
    return options.apiKey;
  }

  // Priority 2: OAuth token from OpenCode auth.json
  const auth = await loadOpenCodeAuth(instanceUrl);
  if (auth?.type === 'oauth') {
    const oauthManager = new GitLabOAuthManager();

    // Check if token needs refresh
    if (oauthManager.needsRefresh(auth.expires)) {
      try {
        const refreshed = await oauthManager.exchangeRefreshToken({
          instanceUrl,
          refreshToken: auth.refresh,
          clientId,
        });

        // Update stored token under 'gitlab' key (same format as opencode-gitlab-auth plugin)
        const authPath = getOpenCodeAuthPath();
        const authData = JSON.parse(fs.readFileSync(authPath, 'utf-8'));

        authData.gitlab = {
          type: 'oauth',
          refresh: refreshed.refreshToken,
          access: refreshed.accessToken,
          expires: refreshed.expiresAt,
          enterpriseUrl: instanceUrl, // Use enterpriseUrl to match auth plugin format
        };

        fs.writeFileSync(authPath, JSON.stringify(authData, null, 2), { mode: 0o600 });

        return refreshed.accessToken;
      } catch (error) {
        // If refresh fails, fall through to env var but remember the error
        const refreshErrorMsg = error instanceof Error ? error.message : String(error);
        // Silently handle refresh failure — avoid stdout output that interferes with TUI

        // Priority 3: Environment variable (fallback when refresh fails)
        const envApiKey = process.env[options.environmentVariableName];
        if (envApiKey) {
          return envApiKey;
        }

        // No env var fallback — throw with the refresh failure context
        throw new GitLabError({
          message:
            `OAuth token refresh failed and no fallback ${options.environmentVariableName} environment variable is set. ` +
            `Refresh error: ${refreshErrorMsg}. ` +
            `Re-authenticate with 'opencode auth login gitlab' or set ${options.environmentVariableName}.`,
        });
      }
    } else {
      return auth.access;
    }
  }

  // Priority 3: Environment variable
  const apiKey = process.env[options.environmentVariableName];

  if (!apiKey) {
    throw new GitLabError({
      message: `${options.description} API key is missing. Pass it as the 'apiKey' parameter, set the ${options.environmentVariableName} environment variable, or authenticate with 'opencode auth login gitlab'.`,
    });
  }

  return apiKey;
}

function withUserAgentSuffix(
  headers: Record<string, string>,
  suffix: string
): Record<string, string> {
  const userAgent = headers['User-Agent'];
  return {
    ...headers,
    'User-Agent': userAgent ? `${userAgent} ${suffix}` : suffix,
  };
}

export function createGitLab(options: GitLabProviderSettings = {}): GitLabProvider {
  const instanceUrl =
    options.instanceUrl ?? process.env['GITLAB_INSTANCE_URL'] ?? 'https://gitlab.com';
  const providerName = options.name ?? 'gitlab';

  // Cache for the API key - loaded lazily on first use
  let cachedApiKey: string | undefined;
  let apiKeyPromise: Promise<string> | undefined;

  const getApiKey = async (): Promise<string> => {
    if (cachedApiKey) {
      return cachedApiKey;
    }

    if (apiKeyPromise) {
      return apiKeyPromise;
    }

    apiKeyPromise = loadApiKey(
      {
        apiKey: options.apiKey,
        environmentVariableName: 'GITLAB_TOKEN',
        description: 'GitLab',
      },
      instanceUrl,
      options.clientId
    );

    cachedApiKey = await apiKeyPromise;
    apiKeyPromise = undefined;
    return cachedApiKey;
  };

  /**
   * Refresh the API key by clearing the cache and re-fetching from auth provider.
   * This is called when a 401 error occurs to trigger OAuth token refresh.
   */
  const refreshApiKey = async (): Promise<void> => {
    // Clear the cached API key to force a refresh
    cachedApiKey = undefined;
    apiKeyPromise = undefined;

    // Re-fetch the API key, but don't pass the stale options.apiKey
    // This forces loadApiKey to read from auth.json and potentially refresh the OAuth token
    cachedApiKey = await loadApiKey(
      {
        apiKey: undefined, // Bypass stale options.apiKey to force auth.json read
        environmentVariableName: 'GITLAB_TOKEN',
        description: 'GitLab',
      },
      instanceUrl,
      options.clientId
    );
  };

  const getHeaders = () => {
    // For synchronous access, we need to have the key already loaded
    // or fall back to env var/explicit key
    const apiKey = cachedApiKey || options.apiKey || process.env['GITLAB_TOKEN'] || '';

    if (!apiKey) {
      throw new GitLabError({
        message:
          "GitLab API key is missing. Pass it as the 'apiKey' parameter, set the GITLAB_TOKEN environment variable, or authenticate with 'opencode auth login gitlab'.",
      });
    }

    return withUserAgentSuffix(
      {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
      `ai-sdk-gitlab/${VERSION}`
    );
  };

  // Pre-load the API key asynchronously
  getApiKey().catch(() => {
    // Silently fail - will be caught when getHeaders is called
  });

  const createAgenticChatModel = (modelId: string, agenticOptions?: GitLabAgenticOptions) => {
    const mapping = getModelMapping(modelId);

    if (!mapping) {
      throw new GitLabError({
        message: `Unknown model ID: ${modelId}. Model must be registered in MODEL_MAPPINGS.`,
      });
    }

    if (agenticOptions?.providerModel) {
      const validModels = getValidModelsForProvider(mapping.provider);
      if (!validModels.includes(agenticOptions.providerModel)) {
        throw new GitLabError({
          message: `Invalid providerModel '${agenticOptions.providerModel}' for provider '${mapping.provider}'. Valid models: ${validModels.join(', ')}`,
        });
      }
    }

    const featureFlags = {
      DuoAgentPlatformNext: true as const,
      ...options.featureFlags,
      ...agenticOptions?.featureFlags,
    };

    const defaultAiGatewayHeaders = {
      'User-Agent': `gitlab-ai-provider/${VERSION}`,
    };

    const aiGatewayHeaders = {
      ...defaultAiGatewayHeaders,
      ...options.aiGatewayHeaders,
      ...agenticOptions?.aiGatewayHeaders,
    };

    const baseConfig = {
      provider: `${providerName}.agentic`,
      instanceUrl,
      getHeaders,
      refreshApiKey,
      fetch: options.fetch,
      maxTokens: agenticOptions?.maxTokens,
      featureFlags,
      aiGatewayUrl: options.aiGatewayUrl,
      aiGatewayHeaders,
    };

    if (mapping.provider === 'openai') {
      return new GitLabOpenAILanguageModel(modelId, {
        ...baseConfig,
        openaiModel: agenticOptions?.providerModel ?? mapping.model,
      });
    }

    return new GitLabAnthropicLanguageModel(modelId, {
      ...baseConfig,
      anthropicModel: agenticOptions?.providerModel ?? mapping.model,
    });
  };

  const createWorkflowChatModel = (
    modelId: string,
    workflowOptions?: GitLabWorkflowOptions
  ): GitLabWorkflowLanguageModel => {
    const mapping = getModelMapping(modelId);

    if (!mapping || mapping.provider !== 'workflow') {
      throw new GitLabError({
        message: `Unknown workflow model ID: ${modelId}. Use 'duo-workflow' or a 'duo-workflow-*' model ID.`,
      });
    }

    return new GitLabWorkflowLanguageModel(
      modelId,
      {
        provider: `${providerName}.workflow`,
        instanceUrl,
        getHeaders,
        refreshApiKey,
        fetch: options.fetch,
        featureFlags: {
          ...options.featureFlags,
          ...workflowOptions?.featureFlags,
        },
        aiGatewayUrl: options.aiGatewayUrl,
      },
      workflowOptions
    );
  };

  // Default model factory - uses Anthropic backend for tool support
  const createDefaultModel = (modelId: string): LanguageModelV2 => {
    // Route workflow models to the workflow backend
    if (isWorkflowModel(modelId)) {
      return createWorkflowChatModel(modelId);
    }
    // Use Anthropic-based model by default for all models to get tool support
    return createAgenticChatModel(modelId);
  };

  const provider = Object.assign((modelId: string) => createDefaultModel(modelId), {
    specificationVersion: 'v2' as const,
    languageModel: createDefaultModel,
    chat: createDefaultModel,
    agenticChat: createAgenticChatModel,
    workflowChat: createWorkflowChatModel,
  }) as GitLabProvider;

  // Unsupported model types
  provider.textEmbeddingModel = (modelId: string) => {
    throw new GitLabError({
      message: `GitLab provider does not support text embedding models. Model ID: ${modelId}`,
    });
  };

  provider.imageModel = (modelId: string) => {
    throw new GitLabError({
      message: `GitLab provider does not support image models. Model ID: ${modelId}`,
    });
  };

  return provider;
}

/**
 * Default GitLab Duo provider instance
 *
 * @example
 * ```typescript
 * import { gitlab } from '@ai-sdk/gitlab';
 *
 * const model = gitlab('duo-chat');
 * ```
 */
export const gitlab = createGitLab();
